import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { HealthModels, PolicyModels, QuotationModels } from '@wz-models';
import { getDateUtc } from '@wz-utilities/date.utilities';
import { getControlWithValueChanges } from '@wz-utilities/form.utilities';
import { WzDateValidators } from '@wz-validators';
import { subYears } from 'date-fns';
import {
  FormGroupBaseComponent,
  getFormGroupBaseDirective,
} from 'libs/modules/tunnelsante-forms/components/form-group-base.component';
import moment from 'moment';
import {
  factoryAgeValidators,
  getMinMaxAgeByPackage,
} from '../health-quotation-form/factory-age-validators';

@Component({
  selector: 'wz-health-family-form',
  templateUrl: './health-family-form.component.html',
  styleUrls: ['./health-family-form.component.scss'],
  standalone: false,
  providers: [getFormGroupBaseDirective(HealthFamilyFormComponent)],
})
export class HealthFamilyFormComponent
  extends FormGroupBaseComponent<HealthModels.Family>
  implements OnChanges
{
  @Input() set dateEffet(value: Date) {
    this._dateEffet = value;
    let control = this.formGroup.controls['enfants'] as FormArray;
    if (control) {
      control.controls.forEach((c) => this.childAgeValidator()(c));
    }

    if (this.conjointCtrl) {
      this.patchConjointBirthdayValidators(true);
    }
  }

  @Input() disabled!: boolean;
  @Input() package!: PolicyModels.PackageEnum;
  @Input() formula!: QuotationModels.FormulaEnum;

  @Input() regimeSocial!: HealthModels.RegimeSocialEnum;
  private _dateEffet!: Date;

  public conjointCtrl!: FormGroup | any;
  public enfantsCtrl!: FormArray | any;

  minAgeConjoint!: number;
  maxAgeConjoint!: number;

  constructor(private fb: FormBuilder) {
    super();
    this.formGroup = this.createFormGroup();
  }

  olderThan80(date: string): any {
    if (date) {
      return moment().diff(moment(date, 'dd/MM/yyyy'), 'years') >= 80;
    }
  }

  ngOnChanges(changes: SimpleChanges | any) {
    if (changes['regimeSocial']) {
      if (
        this.conjointCtrl &&
        !this.conjointCtrl.value.isRegimeSocialDifferent
      ) {
        this.conjointCtrl.patchValue({ regimeSocial: this.regimeSocial });
      }

      this.enfantsCtrl.controls.forEach((enfantCtrl: any) => {
        if (!enfantCtrl.value.isRegimeSocialDifferent) {
          enfantCtrl.patchValue({ regimeSocial: this.regimeSocial });
        }
      });
    }

    if (
      changes.formula &&
      this.package == PolicyModels.PackageEnum.Sante_Fidelity
    ) {
      if (this.conjointCtrl) {
        this.patchConjointBirthdayValidators(true);
      }
    }
  }

  addConjoint() {
    this.conjointCtrl = this.getPersonFormGroup(true);
    this.patchConjointBirthdayValidators();
    this.conjointCtrl.updateValueAndValidity();
    this.formGroup.addControl('conjoint', this.conjointCtrl);
  }

  removeConjoint() {
    this.conjointCtrl = null;
    this.formGroup.removeControl('conjoint');
  }

  addChild() {
    this.enfantsCtrl.push(this.getPersonFormGroup(false, true));
  }

  removeChild(i: number) {
    this.enfantsCtrl.removeAt(i);
  }

  private createFormGroup() {
    this.enfantsCtrl = this.fb.array([]);
    const group = this.fb.group({
      enfants: this.enfantsCtrl,
    });

    return group;
  }

  private getPersonFormGroup(
    shouldBeAdult = false,
    canBeDisabled = false,
  ): FormGroup {
    const rsCtrl = this.fb.control(this.regimeSocial, Validators.required);
    rsCtrl.disable();
    const personFormGroup: any = this.fb.group({
      dateNaissance: this.getDateNaissanceCtrl(shouldBeAdult),
      regimeSocial: rsCtrl,
      isRegimeSocialDifferent: getControlWithValueChanges(
        this.fb,
        false,
        [],
        (fc) => {
          const isDifferent = fc.value;
          if (isDifferent) {
            rsCtrl.enable();
          } else {
            rsCtrl.disable();
            rsCtrl.setValue(this.regimeSocial);
          }
        },
        this.destroy$,
      ),
    });

    if (canBeDisabled) {
      personFormGroup.addControl('isDisabled', this.fb.control(false));
      personFormGroup.setValidators(this.childAgeValidator());
    }

    return personFormGroup;
  }

  public childAgeValidator(): ValidatorFn | any {
    return (group: FormGroup): ValidationErrors => {
      const control1 = group.controls['dateNaissance'];
      const control2 = group.controls['isDisabled'];
      if (control1 && control1.value && this._dateEffet) {
        let dateUtc = getDateUtc(control1.value);
        if (dateUtc) {
          let anneeDateNaissance = dateUtc.getFullYear();
          let anneeDateEffetMoins26 = subYears(
            this._dateEffet,
            26,
          ).getFullYear();
          let test = anneeDateNaissance < anneeDateEffetMoins26;
          if (test && !control2.value) control1.setErrors({ childAge: true });
          if (test && control2.value) control1.setErrors(null);
        }
      } else {
        if (control1) {
          control1.setErrors(null);
          control1.updateValueAndValidity({ emitEvent: false, onlySelf: true });
        }
      }
      return {};
    };
  }

  private getDateNaissanceCtrl(shouldBeAdult = false): FormControl {
    let validators = [
      Validators.required,
      WzDateValidators.max(getDateUtc() ?? new Date()),
    ];
    return this.fb.control('', validators);
  }

  override onPatchValue(
    family: HealthModels.Family,
    opt: {
      dateEffet: Date;
      formula: QuotationModels.FormulaEnum;
    },
  ): void {
    this.dateEffet = opt.dateEffet;
    this.formula = opt.formula;

    if (family.conjoint) {
      this.addConjoint();
    }

    if (family.enfants) {
      if (this.enfantsCtrl) {
        this.enfantsCtrl.controls.splice(0);
      }

      family.enfants.forEach((_enfant) => {
        this.addChild();
      });
    }

    this.formGroup.patchValue(family);
  }

  private patchConjointBirthdayValidators(doUpdate?: boolean) {
    const ages = getMinMaxAgeByPackage(this.package, this.formula);
    this.minAgeConjoint = ages.minAge;
    this.maxAgeConjoint = ages.maxAge;
    const validators = factoryAgeValidators(
      this._dateEffet,
      ages.minAge,
      ages.maxAge,
    );
    this.conjointCtrl.get('dateNaissance').setValidators(validators);

    if (doUpdate) {
      this.conjointCtrl.get('dateNaissance').updateValueAndValidity();
    }
  }
}
